import { assign, createMachine } from 'xstate';
import { billingContext, BillingDoneData, billingMachine } from '~/machines/billing-selection';
import {
  enrollmentContext,
  EnrollmentDoneData,
  enrollmentMachine,
} from '~/machines/enrollment-selection';
import { ParsedQueryParams } from '~/providers/query-params/types';
import { createProductLead, redirectToDashboard } from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { UTILITY_IDS } from '~/utils/constants';
import { getInitialState } from '~/utils/funnels';
import { config } from './config';

export const funnelMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as Context,
        services: {} as Services,
      },
      context: {} as Context,
      id: config.id,
      initial: getInitialState(config.id, ['enrollment', 'billing']) || 'start',
      entry: 'setFunnelPacket',
      states: {
        start: {
          always: [{ cond: 'hasVerifiedCredentials', target: 'billing' }, { target: 'enrollment' }],
        },
        enrollment: {
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              canSkipUtilityConnect: true,
              enrollmentOptions: ['utility_connect'],
              funnelPacket: funnelContext.funnelPacket,
            })),
            onDone: [
              { cond: 'skippedUtilityConnect', target: 'completed.redirecting' },
              { target: 'billing' },
            ],
          },
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              billingOptions: ['acb'],
              funnelPacket: funnelContext.funnelPacket,
            })),
            onDone: 'completed',
          },
        },
        completed: {
          id: 'completed',
          initial: 'assigning',
          states: {
            assigning: {
              invoke: {
                id: 'createLead',
                src: 'createLead',
                onDone: 'redirecting',
                onError: '#unexpected',
              },
            },
            redirecting: {
              type: 'final',
              onEntry: 'redirectToDashboard',
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
      },
    },
    {
      actions: {
        redirectToDashboard: context => {
          redirectToDashboard({
            userId: context.funnelPacket!.user.id,
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
          });
        },
        setFunnelPacket: assign(context => ({
          funnelPacket: context.funnelPacket || context.queryParams!.resumingUser!.packet,
        })),
      },
      guards: {
        hasVerifiedCredentials: context =>
          context.funnelPacket.utilityCredential.verificationStatus === 'correct',
        skippedUtilityConnect: (_context, event) => event.data.enrollmentSelection === 'none',
      },
      services: {
        billingMachine: billingMachine(),
        createLead: async context => {
          const utilityId = context.funnelPacket!.utility.id;
          if (UTILITY_IDS['Ameren MO'] !== utilityId) {
            await createProductLead({
              planId: context.funnelPacket.customPlan.id,
              utilityAccountId: context.funnelPacket.utilityAccount.id,
              productId: 3, // BillPayService
            });
          }
          return undefined;
        },
        enrollmentMachine: enrollmentMachine(),
      },
    },
  );

type Context = {
  funnelPacket: FetchFunnelPacketResponse;
  queryParams: ParsedQueryParams;
};

type Services = {
  billingMachine: { data: BillingDoneData };
  createBillPayLead: { data: undefined };
  enrollmentMachine: { data: EnrollmentDoneData };
};
